import { Container, Graphics, Sprite, Texture, TilingSprite } from 'pixi.js';
import { gsap } from 'gsap';

const MAP_WIDTH = 1024;
const MAP_HEIGHT = 512;

/**
 * The animated globe.
 *
 * The surface is a wrapping procedural world map generated once at boot from
 * periodic value noise, scrolled inside a circular mask. Over it sit a
 * terminator gradient, a specular highlight and an atmospheric rim - the three
 * cues that sell a sphere in two dimensions.
 *
 * Deliberately not a 3D sphere: this runs inside the same PixiJS scene graph as
 * everything else, so it composites with the bloom and the camera for free, and
 * it cannot fail to compile on a headless VPS GPU the way a custom shader can.
 */
export class AnimatedEarth {
  readonly view = new Container();

  private readonly surface: TilingSprite;
  private readonly atmosphere: Sprite;
  private readonly highlight: Sprite;
  private readonly shadow: Sprite;
  private readonly grid: Graphics;
  /** Width in local pixels of one full trip around the map. */
  private readonly mapSpan: number;

  private spin = 0.012;

  constructor(private readonly radius = 320) {
    const map = createWorldMapTexture();
    // Uniform tile scale keeps the 2:1 equirectangular map undistorted; the
    // globe therefore shows half the world at a time, as a sphere should.
    const tileScale = (radius * 2) / MAP_HEIGHT;
    this.mapSpan = MAP_WIDTH * tileScale;

    this.surface = new TilingSprite({
      texture: map,
      width: radius * 2,
      height: radius * 2,
    });
    this.surface.position.set(-radius, -radius);
    this.surface.tileScale.set(tileScale);

    const mask = new Graphics().circle(0, 0, radius).fill(0xffffff);

    this.grid = new Graphics();
    this.drawGraticule();
    this.grid.alpha = 0.16;

    this.shadow = new Sprite(createTerminatorTexture());
    this.shadow.anchor.set(0.5);
    this.shadow.width = radius * 2;
    this.shadow.height = radius * 2;

    this.highlight = new Sprite(createHighlightTexture());
    this.highlight.anchor.set(0.5);
    this.highlight.width = radius * 2;
    this.highlight.height = radius * 2;
    this.highlight.blendMode = 'add';
    this.highlight.alpha = 0.5;

    // One container, one mask: sharing a single mask object between several
    // display objects is not reliable, and four stencil passes would be waste.
    const globe = new Container();
    globe.addChild(this.surface, this.grid, this.shadow, this.highlight);
    globe.mask = mask;

    this.atmosphere = new Sprite(createAtmosphereTexture());
    this.atmosphere.anchor.set(0.5);
    this.atmosphere.width = radius * 2.7;
    this.atmosphere.height = radius * 2.7;
    this.atmosphere.blendMode = 'add';
    this.atmosphere.alpha = 0.62;

    this.view.addChild(this.atmosphere, globe, mask);
    this.view.eventMode = 'none';
  }

  get size(): number {
    return this.radius;
  }

  /** Rotate so a longitude faces the camera. Degrees, east positive. */
  lookAt(longitude: number, duration = 2.4): gsap.core.Tween {
    const normalised = (((longitude + 180) % 360) + 360) % 360;
    return gsap.to(this.surface.tilePosition, {
      x: -(normalised / 360) * this.mapSpan,
      duration,
      ease: 'power2.inOut',
    });
  }

  setSpinSpeed(speed: number): void {
    gsap.to(this, { spin: speed, duration: 1.4, ease: 'sine.inOut' });
  }

  setAtmosphereColor(color: number): void {
    gsap.to(this.atmosphere, { tint: color, duration: 1.6 });
  }

  update(deltaSeconds: number): void {
    this.surface.tilePosition.x -= this.spin * this.mapSpan * deltaSeconds;
    this.atmosphere.rotation += deltaSeconds * 0.03;
  }

  private drawGraticule(): void {
    const r = this.radius;
    this.grid.clear();

    for (let i = 1; i < 6; i++) {
      const y = -r + (i * 2 * r) / 6;
      const halfWidth = Math.sqrt(Math.max(0, r * r - y * y));
      this.grid.moveTo(-halfWidth, y).lineTo(halfWidth, y);
    }
    for (let i = 1; i < 8; i++) {
      const x = -r + (i * 2 * r) / 8;
      this.grid.moveTo(x, -Math.sqrt(Math.max(0, r * r - x * x)));
      this.grid.lineTo(x, Math.sqrt(Math.max(0, r * r - x * x)));
    }

    this.grid.stroke({ width: 1.4, color: 0x8fd4ff, alpha: 0.8 });
  }
}

function context(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas is unavailable - cannot build the globe');
  return ctx;
}

/**
 * Value noise on a lattice that wraps in x.
 *
 * Wrapping matters: the map is scrolled continuously, so a seam would ride
 * across the globe once per rotation.
 */
function makeNoise(cellsX: number, cellsY: number, seed: number): (x: number, y: number) => number {
  const lattice = new Float32Array(cellsX * cellsY);
  let state = seed >>> 0;
  for (let i = 0; i < lattice.length; i++) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    lattice[i] = state / 4294967296;
  }

  const at = (ix: number, iy: number): number => {
    const wx = ((ix % cellsX) + cellsX) % cellsX;
    const wy = Math.min(cellsY - 1, Math.max(0, iy));
    return lattice[wy * cellsX + wx] ?? 0;
  };

  const smooth = (t: number): number => t * t * (3 - 2 * t);

  return (x, y) => {
    const fx = x * cellsX;
    const fy = y * cellsY;
    const ix = Math.floor(fx);
    const iy = Math.floor(fy);
    const tx = smooth(fx - ix);
    const ty = smooth(fy - iy);

    const a = at(ix, iy);
    const b = at(ix + 1, iy);
    const c = at(ix, iy + 1);
    const d = at(ix + 1, iy + 1);

    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  };
}

function createWorldMapTexture(): Texture {
  const ctx = context(MAP_WIDTH, MAP_HEIGHT);
  const image = ctx.createImageData(MAP_WIDTH, MAP_HEIGHT);
  const data = image.data;

  const octaves = [
    { noise: makeNoise(8, 5, 0x9e3779b9), weight: 0.55 },
    { noise: makeNoise(17, 9, 0x85ebca6b), weight: 0.26 },
    { noise: makeNoise(37, 19, 0xc2b2ae35), weight: 0.13 },
    { noise: makeNoise(71, 37, 0x27d4eb2f), weight: 0.06 },
  ];

  for (let py = 0; py < MAP_HEIGHT; py++) {
    const v = py / MAP_HEIGHT;
    // Squeeze land away from the poles so the ice caps read correctly.
    const polar = Math.abs(v - 0.5) * 2;

    for (let px = 0; px < MAP_WIDTH; px++) {
      const u = px / MAP_WIDTH;

      let height = 0;
      for (const octave of octaves) height += octave.noise(u, v) * octave.weight;
      height = height * (1 - polar * 0.22) + (polar > 0.94 ? 0.35 : 0);

      const index = (py * MAP_WIDTH + px) * 4;
      let r: number;
      let g: number;
      let b: number;

      if (polar > 0.955) {
        // Ice
        r = 232;
        g = 240;
        b = 248;
      } else if (height < 0.47) {
        // Ocean, deepening away from the shelf
        const depth = (0.47 - height) / 0.47;
        r = 8 + (1 - depth) * 22;
        g = 34 + (1 - depth) * 62;
        b = 78 + (1 - depth) * 92;
      } else if (height < 0.5) {
        // Shelf
        r = 32;
        g = 108;
        b = 150;
      } else if (height < 0.62) {
        // Lowland
        r = 40;
        g = 104;
        b = 62;
      } else if (height < 0.7) {
        // Highland
        r = 96;
        g = 112;
        b = 58;
      } else {
        // Peaks
        const t = Math.min(1, (height - 0.7) / 0.2);
        r = 128 + t * 90;
        g = 118 + t * 90;
        b = 100 + t * 90;
      }

      data[index] = r;
      data[index + 1] = g;
      data[index + 2] = b;
      data[index + 3] = 255;
    }
  }

  ctx.putImageData(image, 0, 0);
  return Texture.from(ctx.canvas);
}

/** Day/night shading, dark on the lower right. */
function createTerminatorTexture(): Texture {
  const size = 512;
  const ctx = context(size, size);
  const gradient = ctx.createRadialGradient(
    size * 0.34,
    size * 0.3,
    size * 0.06,
    size * 0.5,
    size * 0.5,
    size * 0.62,
  );
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(0.52, 'rgba(2,6,16,0.28)');
  gradient.addColorStop(1, 'rgba(1,3,10,0.86)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(ctx.canvas);
}

/** Specular sheen where the light hits. */
function createHighlightTexture(): Texture {
  const size = 512;
  const ctx = context(size, size);
  const gradient = ctx.createRadialGradient(
    size * 0.33,
    size * 0.28,
    0,
    size * 0.33,
    size * 0.28,
    size * 0.42,
  );
  gradient.addColorStop(0, 'rgba(180,225,255,0.55)');
  gradient.addColorStop(0.45, 'rgba(120,180,255,0.16)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(ctx.canvas);
}

/** Rim glow that reads as atmosphere. */
function createAtmosphereTexture(): Texture {
  const size = 512;
  const ctx = context(size, size);
  const gradient = ctx.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.34,
    size / 2,
    size / 2,
    size * 0.5,
  );
  gradient.addColorStop(0, 'rgba(56,189,248,0)');
  gradient.addColorStop(0.62, 'rgba(56,189,248,0.42)');
  gradient.addColorStop(0.78, 'rgba(120,215,255,0.22)');
  gradient.addColorStop(1, 'rgba(56,189,248,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(ctx.canvas);
}
